/**
 * Rail "From Designs" select feedback: pending while onSelectStyle/hydrate
 * runs; terminal failed on reject or ceiling; idle on success.
 * Glyph stays Loader2 (pending) / ImageOff (failed) — SquareLoader is separate.
 */

export const SELECT_FEEDBACK_CEILING_MS = 8_000;

export type SelectFeedbackPhase = "idle" | "pending" | "failed";

export type SelectFeedbackState = {
  phase: SelectFeedbackPhase;
  styleId: string | null;
};

export type SelectFeedbackController = {
  select: (styleId: string) => void;
  getState: () => SelectFeedbackState;
  /** Test helper — advance fake timers externally. */
  _ceilingMs: number;
};

/**
 * Mirrors StudioRailUpload handleDesignSelect terminal semantics.
 */
export function createSelectFeedbackController(opts: {
  ceilingMs?: number;
  onSelectStyle?: (styleId: string) => void | Promise<void>;
  onChange?: (state: SelectFeedbackState) => void;
}): SelectFeedbackController {
  const ceilingMs = opts.ceilingMs ?? SELECT_FEEDBACK_CEILING_MS;
  let phase: SelectFeedbackPhase = "idle";
  let styleId: string | null = null;
  let ceilingTimer: ReturnType<typeof setTimeout> | null = null;

  const emit = () => {
    opts.onChange?.({ phase, styleId });
  };

  const clearCeiling = () => {
    if (ceilingTimer) {
      clearTimeout(ceilingTimer);
      ceilingTimer = null;
    }
  };

  const toFailed = (id: string) => {
    clearCeiling();
    phase = "failed";
    styleId = id;
    emit();
  };

  const toIdle = () => {
    clearCeiling();
    phase = "idle";
    styleId = null;
    emit();
  };

  const select = (id: string) => {
    clearCeiling();
    phase = "pending";
    styleId = id;
    emit();

    ceilingTimer = setTimeout(() => {
      ceilingTimer = null;
      if (phase === "pending" && styleId === id) {
        toFailed(id);
      }
    }, ceilingMs);

    void Promise.resolve(opts.onSelectStyle?.(id)).then(
      () => {
        if (phase === "pending" && styleId === id) {
          toIdle();
        }
      },
      () => {
        if (phase === "pending" && styleId === id) {
          toFailed(id);
        }
      },
    );
  };

  return {
    select,
    getState: () => ({ phase, styleId }),
    _ceilingMs: ceilingMs,
  };
}
